import { createHref, type Url } from "@src/shims/next/router-runtime";
import Router from "next/router";
import {
  type AnchorHTMLAttributes,
  cloneElement,
  forwardRef,
  isValidElement,
  type MouseEvent,
  type ReactNode,
} from "react";

function isModifiedEvent(event: MouseEvent<HTMLElement>): boolean {
  return !!(
    event.metaKey ||
    event.altKey ||
    event.ctrlKey ||
    event.shiftKey ||
    event.button !== 0
  );
}

function isExternalLink(url: string): boolean {
  return /^[a-zA-Z][a-zA-Z\d+\-.]*:/.test(url);
}

export type LinkProps = Omit<
  AnchorHTMLAttributes<HTMLAnchorElement>,
  "href"
> & {
  href: Url;
  as?: Url;
  replace?: boolean;
  scroll?: boolean;
  shallow?: boolean;
  prefetch?: boolean;
  passHref?: boolean;
  legacyBehavior?: boolean;
  children: ReactNode;
};

const Link = forwardRef<HTMLAnchorElement, LinkProps>(function Link(
  {
    href,
    as,
    replace,
    passHref,
    legacyBehavior,
    onClick,
    children,
    target,
    ...props
  },
  ref,
) {
  const hrefAsString = createHref(as ?? href);

  const handleClick = (event: MouseEvent<HTMLElement>): void => {
    onClick?.(event as unknown as MouseEvent<HTMLAnchorElement>);

    if (event.defaultPrevented) return;
    if (target && target !== "_self") return;
    if (isModifiedEvent(event)) return;
    if (isExternalLink(hrefAsString)) return;

    event.preventDefault();

    if (replace) {
      void Router.replace(href, as);
      return;
    }

    void Router.push(href, as);
  };

  if (legacyBehavior) {
    if (!isValidElement(children)) {
      return null;
    }

    const childProps: Record<string, unknown> = {
      onClick: (event: MouseEvent<HTMLElement>): void => {
        children.props.onClick?.(event);
        handleClick(event);
      },
    };

    if (passHref || children.props.href === undefined) {
      childProps.href = hrefAsString;
    }

    return cloneElement(children, childProps);
  }

  return (
    <a
      {...props}
      href={hrefAsString}
      onClick={handleClick}
      ref={ref}
      target={target}
    >
      {children}
    </a>
  );
});

export default Link;
